import request from './request'
import type {
  Res,
  UserRes,
  DataUpdatesRes,
  UpdateUserReq,
  UpdateUserEmailReq,
  UpdateUserEmailRes,
  UpdatePasswordReq,
  UpdatePasswordRes,
  BindAuthReq,
  BindAuthRes,
  GoogleInfoRes,
  SearchReq,
  ResWithMeta,
  UpdateGlobalConfigReq,
  NotificationApiRes,
  PlansRes,
  ManualServiceRes,
  ManualServiceReq,
  AiMenusRes,
  CreditPlansReq,
  CreditPlansRes,
  StripePlansReq,
  StripePlansRes,
  TSubscribeRes,
  GetDiscountCodeRes,
  WelfareCenterRes,
  WelfareCenterTagEnum,
  WelfareCenterCategoryRes
} from '@/types/api'
import type { AxiosRequestConfig } from 'axios'
import axios from 'axios'

// 获取跨域cookie接口，无 /api/v1 前缀
export const getCsrfCookieApi = async (token: string) => {
  const baseOrigin =
    process.env.NEXT_PUBLIC_BUILD_ENV === 'test' || process.env.NODE_ENV === 'development'
      ? 'https://test.echotik.shop'
      : ''
  const instance = axios.create()
  instance.defaults.baseURL = baseOrigin
  instance.defaults.headers.common['Authorization'] = `Bearer ${token}`
  return instance.get<any>('/api/csrf-cookie')
}

export const getUserApi = async (config?: AxiosRequestConfig) => {
  return request.get<Res<UserRes>>('/users', config)
}

export const updateUserApi = async (params: UpdateUserReq) => {
  return request.put<Res<UserRes>>('/users', params)
}

// 检查系统更新
export const getSystemUpdateApi = async () => {
  return request.get('/system/updates')
}

export const getDataUpdateTimeApi = (config?: AxiosRequestConfig) => {
  return request.get<Res<DataUpdatesRes>>('/data/updates', config)
}

export const updateUserEmailApi = (params: UpdateUserEmailReq) => {
  return request.post<Res<UpdateUserEmailRes>>('/users/verify', params)
}

export const updatePasswordApi = (params: UpdatePasswordReq) => {
  return request.post<Res<UpdatePasswordRes>>('/users/changepwd', params)
}

export const sendEmailApi = (params: { email: string }) => {
  return request.post<Res<UpdatePasswordRes>>('/users/sendmail', params)
}

export const updateGlobalConfigApi = async (params: UpdateGlobalConfigReq) => {
  return request.put<Res<UserRes>>('users/global-config', params)
}

export const getGoogleInfo = (token: string) => {
  return axios.create().get<GoogleInfoRes>('https://www.googleapis.com/oauth2/v3/userinfo', {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: false
  })
}

export const bindAuthApi = (platform: string, params: BindAuthReq) => {
  return request.post<Res<BindAuthRes>>(`/auth/${platform}/binding`, params)
}

// 搜索推荐
export const getSearchHotApi = async (config?: AxiosRequestConfig) => {
  return request.get<Res<any>>('/data/influencers/search/list', config)
}

export const getSearchApi = <T>(
  type: 'influencers' | 'products' | 'videos' | 'lives' | 'sellers' | 'tags',
  params: SearchReq,
  config?: AxiosRequestConfig
) => {
  return request.get<ResWithMeta<T>>(`/data/search/${type}`, Object.assign({ params }, config))
}

// 翻译查询
export const getTranslateApi = async (
  params: { keyword: string; target_lang?: string },
  config?: AxiosRequestConfig
) => {
  return request.get<Res<any>>('/tools/translation', { params, ...config })
}

export const getNotificationApi = async () => {
  return request.get<Res<NotificationApiRes>>('/users/notification')
}

// 收费方案,自定义子账号价格
export const getMemberPlansApi = async (quantity = 0, config?: AxiosRequestConfig) => {
  return request.get<Res<PlansRes>>('/member/plans', Object.assign({ params: { quantity } }, config))
}

// 收费明细
export const getPlansDetailApi = async (config?: AxiosRequestConfig) => {
  return request.get<Res<PlansRes>>('/member/plans/items', config)
}

export const getManualService = async (params: ManualServiceReq) => {
  return request.get<Res<ManualServiceRes>>('/member/manual-trial', { params })
}

export const getAiMenus = async () => {
  return request.get<Res<AiMenusRes>>('/tools/navigation-ai')
}

export const getAiMenuConfig = async () => {
  return request.get<Res<AiMenusRes>>('/common/menu-config')
}

// 购买直播，视频次数
export const getCreditPlans = async (params: CreditPlansReq) => {
  return request.get<Res<CreditPlansRes>>('/member/credit-plans', { params })
}

// 立即购买
export const postStripePlansApi = async (params: StripePlansReq) => {
  return request.post<Res<StripePlansRes>>('/stripe/credits-payment', params)
}

// 管理订阅
export const postManageSubscribeApi = async () => {
  return request.post<Res<TSubscribeRes>>('/stripe/create-portal-session')
}

//成为推荐官
export const getAffiliateApi = async (config?: AxiosRequestConfig) => {
  return request.get<Res<any>>('/users/partner-share', config)
}

// 导出日志
export const getExportLogsApi = (params: { page: number; per_page: number }) => {
  return request.get<Res<any>>('/users/export/records', { params })
}

// 微信支付
export const postWxPayApi = async (params: {
  plan_code: string
  plan_type: string
  quantity: number
  code: string
}) => {
  return request.post<Res<{ code_url: string }>>('/wechat/wxpay', { ...params })
}

// 支付宝支付
export const postAliPayApi = async (params: {
  plan_code: string
  plan_type: string
  quantity: number
  code: string
}) => {
  return request.post<Res<{ pay_info: string; order_id: string }>>('/alipay/pay', { ...params })
}

// 折扣码
export const getDiscountCodeApi = async (params: {
  plan_code: string
  plan_type: string
  quantity: number
  code: string
}) => {
  return request.get<Res<GetDiscountCodeRes>>('/member/discount-price', {
    params
  })
}

// 阿里云验证码
export const postAliyunCaptchaApi = async (params: any) => {
  return request.post<Res<any>>('/tools/captcha', params)
}

// 腾讯云验证码
export const postTencentCaptchaApi = async (params: { rand_str: string; ticket: string; phone: string }) => {
  return request.post<Res<any>>('/tools/captcha', params)
}

// 下载无水印视频
export const getDownloadVideoApi = (id: string) => {
  return request.get<Res<{ url: string }>>(`/tools/video/download?video_id=${id}`)
}

// 下载视频剩余次数
export const getDownloadVideoLeftTimesApi = () => {
  return request.get<
    Res<{
      total_quota_num: number
      remain_quota_num: number
      credits_amount: number
      credits_quota_num: number
    }>
  >('/tools/video/quota')
}

// 获取折扣码
export const getMemberDiscountCodeApi = () => {
  return request.get<Res<{ code: string }>>(`/member/discount-code`)
}

// 获取福利中心
export const getWelfareCenterApi = (params: {
  page: number
  per_page: number
  tag: WelfareCenterTagEnum
  type_id?: string
}) => {
  return request.get<Res<WelfareCenterRes>>(`/welfare/welfare-center/${params.tag}`, {
    params: { page: params.page, per_page: params.per_page, type_id: params.type_id }
  })
}

// 运营干货 分类
export const getWelfareCenterCategoryApi = () => {
  return request.get<Res<WelfareCenterCategoryRes>>('/welfare/welfare-type')
}

// 获取轮播图数据
export const getBannerApi = () => {
  return request.get<Res<{ image: string; url: string }[]>>('/banner/board')
}
